import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Layout, Check } from 'lucide-react';
import { getAllPresets } from '../../utils/presetLayouts';

/**
 * Tiny visual preview of a preset layout — renders the `shape` field as a
 * stack of rows, each row holding N evenly-sized rectangles.
 */
const PresetPreview = ({ shape, isCurrent }) => (
  <div
    className={`flex flex-col gap-1 w-full h-20 rounded-md p-1.5 ${
      isCurrent ? 'bg-purple-100 dark:bg-purple-900/40' : 'bg-slate-100 dark:bg-slate-800'
    }`}
    aria-hidden="true"
  >
    {shape.map((row, rowIdx) => (
      <div
        // eslint-disable-next-line react/no-array-index-key
        key={`row-${rowIdx}`}
        className="flex gap-1 flex-1 min-h-0"
      >
        {row.map((_, cellIdx) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`cell-${rowIdx}-${cellIdx}`}
            className={`flex-1 rounded ${
              isCurrent ? 'bg-purple-500' : 'bg-slate-300 dark:bg-slate-600'
            }`}
          />
        ))}
      </div>
    ))}
  </div>
);

/**
 * PresetLayoutSelector — pick a preset arrangement. Hands back EITHER a
 * flat array (legacy single-row) or a 2D grid (multi-row) to the parent
 * via `onSelectPreset`, which routes to `saveLayout` vs. `saveLayoutGrid`.
 */
const PresetLayoutSelector = ({ isOpen, onClose, currentLayout, onSelectPreset }) => {
  const { language } = useLanguage();
  const presets = getAllPresets();

  const handleSelectPreset = (preset) => {
    // Multi-row presets carry layoutGrid; otherwise pass the flat layout.
    onSelectPreset(preset.layoutGrid || preset.layout);
    onClose();
  };

  const isCurrentPreset = (preset) => {
    if (preset.layoutGrid) return false; // grid match requires equal nested shape — keep it visual only
    return (
      JSON.stringify([...preset.layout].sort()) ===
      JSON.stringify([...(currentLayout || [])].sort())
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layout className="w-5 h-5" />
            {language === 'es' ? 'Diseños Predeterminados' : 'Preset Layouts'}
          </DialogTitle>
          <DialogDescription>
            {language === 'es'
              ? 'Selecciona un diseño preconfigurado para tu estilo de estudio'
              : 'Choose a pre-configured layout for your study style'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 py-4">
          {presets.map((preset) => {
            const isCurrent = isCurrentPreset(preset);
            return (
              <button
                key={preset.id}
                onClick={() => handleSelectPreset(preset)}
                data-testid={`preset-${preset.id}`}
                className={`relative p-4 rounded-xl border-2 text-left transition-all hover:shadow-lg ${
                  isCurrent
                    ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20 shadow-lg'
                    : 'border-slate-200 dark:border-slate-700 hover:border-purple-400'
                }`}
              >
                {isCurrent && (
                  <div className="absolute top-3 right-3">
                    <div className="bg-purple-600 text-white rounded-full p-1">
                      <Check className="w-4 h-4" />
                    </div>
                  </div>
                )}

                <PresetPreview shape={preset.shape} isCurrent={isCurrent} />

                <div className="mt-3 flex items-center gap-2">
                  <span className="text-2xl" aria-hidden="true">{preset.icon}</span>
                  <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
                    {preset.name[language]}
                  </h3>
                </div>

                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1.5">
                  {preset.description[language]}
                </p>

                <div className="flex items-center gap-2 text-[10px] text-slate-500 dark:text-slate-500 mt-3 uppercase tracking-widest">
                  <Layout className="w-3 h-3" />
                  <span>
                    {preset.layout.length} {language === 'es' ? 'paneles' : 'panes'}
                    {preset.layoutGrid ? ` · ${preset.layoutGrid.length} ${language === 'es' ? 'filas' : 'rows'}` : ''}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button onClick={onClose} variant="outline">
            {language === 'es' ? 'Cancelar' : 'Cancel'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PresetLayoutSelector;
